import React, { useState, useMemo, useEffect } from 'react';
import Navbar from "../newcomponents/Navbar";
import { FAQUpdated } from '@components/FAQS';
import Footer from "../newcomponents/Footer";
import styles from './style.module.css';
import Link from 'next/link';
import Head from 'next/head';
import ContentReviewNote from '@components/ContentReviewNote';
import SeoCtaLink from '@components/SeoCtaLink';
import { FREE_TRIAL_APPLICATION_CREDITS } from '@src/config/freeTrial';
import ResumeTailorSection from '@components/ResumeTailorSection';

const ResumeGenerator = () => {
  const [resumeFlow, setResumeFlow] = useState(null);
  const [firstCheck, setFirstCheck] = useState(false);
  const [secondCheck, setSecondCheck] = useState(false);
  const [thirdCheck, setThirdCheck] = useState(false);

  // Simulating setting the checks to true with delays
  useEffect(() => {
    const firstTimeout = setTimeout(() => setFirstCheck(true), 1000); // 1 second delay
    const secondTimeout = setTimeout(() => setSecondCheck(true), 2000); // 2 seconds delay
    const thirdTimeout = setTimeout(() => setThirdCheck(true), 3000); // 3 seconds delay

    // Cleanup function to clear timeouts if the component unmounts
    return () => {
      clearTimeout(firstTimeout);
      clearTimeout(secondTimeout);
      clearTimeout(thirdTimeout);
    };
  }, []);

  const description = `Create and tailor an ATS-friendly resume with AI, job-specific keywords, and editable templates. Start with ${FREE_TRIAL_APPLICATION_CREDITS} free application credits—no credit card required.`;
  const image = `https://production-appsyncstack-companyimages307b450c-17ekd4vq0gwy6.s3.us-west-2.amazonaws.com/lifeshack_opengraph.png`
  const title = `AI Resume Builder for ATS-Friendly Resumes | LifeShack`;

  return (
    <>
      <Head>
          <link
            href="https://fonts.googleapis.com/css?family=Roboto:100,100i,300,300i,400,400i,500,500i,700,700i,900,900i"
            rel="stylesheet"
          />
          <title>{title}</title>
          <link rel="canonical" href="https://www.lifeshack.com/ai-resume/" />
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={`https://www.lifeshack.com/ai-resume/`} />
          <meta property="og:image" content={image} />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={image} />     
      </Head>
      <Navbar />
      <ResumeHero />
      <ResumeDirectAnswer />
      <ContentReviewNote />
      <ResumeExample />
      <ResumeHowItWorks />
      <ResumeTailorSection />
      <ProductResearchLinks />
      <FAQUpdated questionsAndAnswers={questionsAndAnswers} title={"Frequently Asked Questions on Our AI Resume Builder"} />
      <Footer about />    
    </>    
  )
};

function ResumeDirectAnswer() {
  return (
    <section className="bg-white px-6 py-12">
      <div className="mx-auto max-w-4xl rounded-2xl border border-indigo-100 bg-indigo-50/60 p-6 sm:p-8">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          What does an AI resume builder do?
        </h2>
        <p className="mt-4 text-lg leading-8 text-gray-700">
          An AI resume builder turns the truthful experience in your existing resume into a clearer,
          role-specific draft using the job description as context. LifeShack can suggest relevant wording and
          formatting, but you stay responsible for reviewing every line and removing anything that is inaccurate.
        </p>
      </div>
    </section>
  );
}

function ResumeExample() {
  return (
    <section className="bg-slate-50 px-6 py-16">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-indigo-700">Illustrative example</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
            From a generic resume bullet to a job-specific draft
          </h2>
          <p className="mt-4 text-lg leading-8 text-gray-600">
            The goal is clearer relevance, not invented experience. Start with facts you can verify, then use the
            target job description to decide which details deserve emphasis.
          </p>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <ExampleCard
            label="Candidate-provided facts"
            text="Answered customer questions by email and chat, documented recurring billing issues, and shared weekly trends with product and billing teams."
          />
          <ExampleCard
            label="Generic bullet"
            text="Helped customers with account problems and answered questions."
          />
          <ExampleCard
            label="Tailored draft"
            text="Resolved customer account questions across email and chat, documented recurring billing issues, and shared weekly trends with product and billing teams."
            highlighted
          />
        </div>

        <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-5 text-gray-700">
          <strong className="text-gray-900">Accuracy check:</strong> only keep channels, responsibilities, tools,
          and results that are true for you. If the job description asks for something you have not done, do not add
          it to the resume.
        </div>

        <p className="mt-8 text-lg text-gray-700">
          Once the resume is ready, you can{' '}
          <Link href="/ai-cover-letter/">
            <a className="font-semibold text-indigo-700 underline decoration-indigo-300 underline-offset-4">
              create a matching AI cover-letter draft
            </a>
          </Link>{' '}
          from the same verified experience.
        </p>
      </div>
    </section>
  );
}

function ExampleCard({
  label,
  text,
  highlighted = false,
}: {
  label: string;
  text: string;
  highlighted?: boolean;
}) {
  return (
    <div className={`rounded-2xl border p-6 shadow-sm ${highlighted ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">{label}</h3>
      <p className="mt-3 leading-7 text-gray-800">{text}</p>
    </div>
  );
}

function ResumeHowItWorks() {
  return (
    <section className="relative isolate overflow-hidden bg-white">
      {/* top gradient wash to connect with hero */}
      <div className="pointer-events-none absolute inset-x-0 -top-20 -z-10 h-40 bg-gradient-to-b from-indigo-50/70 to-transparent" />

      <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
        {/* Lead block */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> AI Resume Builder
          </p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Build a resume that reflects the role you want
          </h2>
          <p className="mt-4 text-lg leading-7 text-gray-600">
            Use your existing resume and a job description to create an editable, role-specific draft. Review every
            suggestion for accuracy before applying; no resume builder can guarantee an employer&apos;s screening result.
          </p>
        </div>

        {/* Divider */}
        <div className="mx-auto mt-10 h-px w-full max-w-5xl bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

        {/* How it works */}
        <div className="mx-auto mt-12 max-w-4xl text-center">
          <h3 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
            How Our AI Resume Writer Creates a Role-Specific Draft
          </h3>
          <p className="mt-3 text-gray-600">
            Go from a generic document to a role-specific resume draft by emphasizing verified skills and experience
            that are relevant to the job description.
          </p>
        </div>

        <ol className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2">
          <Step
            number={1}
            title="Compare the Resume with the Job Description"
            body={
              <>
                Add the target job description so the builder can identify relevant responsibilities, skills, and
                terminology. Treat these as prompts for truthful edits, not instructions to copy keywords or add
                qualifications you do not have.
              </>
            }
          />

          <Step
            number={2}
            title="AI-Powered Resume Writing"
            body={
              <>
                The AI resume writer can suggest clearer professional summaries and work-experience bullets using the
                facts you provide. Keep measurable results only when you can verify them.
              </>
            }
          />

          <Step
            number={3}
            title="Professional, ATS-Friendly Formatting"
            body={
              <>
                Choose a clean, readable template and preview the final formatting before you download or submit it.
                Straightforward headings and consistent structure make the resume easier for people to review.
              </>
            }
          />

          <Step
            number={4}
            title="Create Tailored Versions"
            body={
              <>
                The era of the one-size-fits-all resume is over. Create role-specific versions using the job
                description as context, then review every edit against your actual experience before downloading.
              </>
            }
          />
        </ol>
      </div>
    </section>
  );
}

function Step({
  number,
  title,
  body,
}: {
  number: number;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <li className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-7">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
          <span className="text-base font-semibold">{number}</span>
        </div>
        <div>
          <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
          <p className="mt-2 text-gray-600 leading-relaxed">{body}</p>
        </div>
      </div>
    </li>
  );
}

export default ResumeGenerator;

export const faqTitle = "Frequently Asked Questions on Our AI Resume Builder";

export const questionsAndAnswers = [
  {
    question: "How does the AI optimize my resume for ATS?",
    answer:
      "Our AI compares your resume with the job description you provide. It identifies relevant keywords, skills, and qualifications and suggests edits you can review. No tool can guarantee that a resume will pass an employer's screening process."
  },
  {
    question: "Can I upload my existing resume to get started?",
    answer:
      "Yes. You don’t have to start from scratch. Upload your current resume in PDF or DOCX format, and LifeShack can parse the information into the builder. From there, you can edit and reformat it using a professional template."
  },
  {
    question: "How is this different from a standard resume template?",
    answer:
      "A standard template mainly provides layout. An AI resume builder can also suggest edits to summaries and bullet points using your resume and the target job description. You still decide which suggestions are accurate and useful."
  },
  {
    question: "Will the AI resume builder invent experience or skills?",
    answer:
      "It should not. The draft depends on the information you provide, but generated wording can still be wrong or overstate a detail. Check every role, skill, tool, date, and result against your real experience before using the resume."
  },
  {
    question: "Will recruiters know I used an AI resume builder?",
    answer:
      "The downloaded resume has no LifeShack watermark. AI is a writing and formatting aid, so review the final document and make sure every statement accurately reflects your experience."
  },
  {
    question: "Should I tailor my resume for every job?",
    answer:
      "You do not need to rewrite everything for every application. For priority roles, adjust the summary, skills, and most relevant experience so they clearly reflect the job's real requirements while keeping your underlying facts consistent."
  },
  {
    question: "Can the AI create a resume for any industry or career level?",
    answer:
      "The builder can help with many industries and career levels by using the resume details and job description you provide. Its output still needs your review for industry-specific accuracy, terminology, and context. Afterward, you can use the same verified experience to draft a matching cover letter."
  }
];

function ProductResearchLinks() {
  return (
    <section className="bg-slate-50 px-6 py-14">
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-3xl font-bold text-gray-900">
          Decide whether you need resume help or application automation.
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          Compare trackers, autofill extensions, resume tools, and cloud
          application agents before choosing a workflow.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          <Link href="/ai-auto-apply/">
            <a className="font-semibold text-indigo-700">
              Automatically apply to jobs with AI
            </a>
          </Link>
          <Link href="/ai-cover-letter/">
            <a className="font-semibold text-indigo-700">
              Create an AI cover letter
            </a>
          </Link>
          <Link href="/best-ai-auto-apply-tools/">
            <a className="font-semibold text-indigo-700">
              Best AI auto-apply tools
            </a>
          </Link>
        </div>
      </div>
    </section>
  );
}


function ResumeHero({ templates = [] }: { templates?: any[] } = {}) {
  const fallbacks = useMemo(
    () => [
      {
        id: "modern",
        name: "Modern",
        image: "/templates/modern.png",
        thumb:
          "data:image/svg+xml;charset=utf8," +
          encodeURIComponent(sampleSVG("M")),
      },
      {
        id: "graduate",
        name: "Graduate",
        image: "/templates/graduate.png",
        thumb:
          "data:image/svg+xml;charset=utf8," +
          encodeURIComponent(sampleSVG("C")),
      },
      {
        id: "timeless",
        name: "Timeless",
        image: "/templates/timeless.png",
        thumb:
          "data:image/svg+xml;charset=utf8," +
          encodeURIComponent(sampleSVG("Mi")),
      },
    ],
    []
  );

  const merged = templates.length ? templates : fallbacks;
  const [active, setActive] = useState(merged[0]?.id ?? "modern");
  const activeTemplate = useMemo(
    () => merged.find((t) => t.id === active) || merged[0],
    [merged, active]
  );

  return (
    <section className={styles.hero}>
      <div className={styles.container}>
        {/* Left: Copy */}
        <div className={styles.copy}>
          <div className={styles.badge}>
            <span className={styles.dot} /> AI Resume Builder
          </div>

          <h1 className={styles.title}>Build an ATS-Friendly Resume with AI</h1>
          <p className={styles.subtitle}>
            Turn your existing work history and a job description into an editable, role-specific resume draft.
            Review every suggested change before you export or apply.
          </p>

          <ul className={styles.features}>
            {[
              "ATS‑friendly formatting",
              "One‑click tailoring for each job",
              "PDF & DOCX export",
            ].map((item) => (
              <li key={item} className={styles.featureItem}>
                <CheckIcon />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className={styles.ctaRow}>
            <SeoCtaLink
              href="/signup/?utm_source=seo&utm_medium=organic&utm_campaign=resume&utm_content=ai-resume"
              pageType="resume"
              placement="hero"
              className={`${styles.btn} ${styles.btnPrimary}`}
            >
              Start with free application credits
            </SeoCtaLink>
          </div>

          <div className={styles.socialProof}>
            <div>Editable before export</div>
            <div className={styles.divider} />
            <div>No LifeShack watermark</div>
          </div>
        </div>

        {/* Right: Preview */}
        <div className={styles.previewWrap}>
          <div className={styles.previewCard}>
            <div className={styles.preview}>
              {activeTemplate && (
                <img
                  key={activeTemplate.id}
                  src={activeTemplate.image}
                  alt={`${activeTemplate.name} resume template preview`}
                  className={[styles.previewImg, styles.fadeIn].join(" ")}
                />
              )}
              {/* <div className={[styles.label, styles.fadeDown].join(" ")}>{activeTemplate?.name} Template</div> */}
            </div>
          </div>

          <div id="templates" className={styles.thumbRow}>
            {merged.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActive(t.id)}
                aria-pressed={t.id === active}
                className={[styles.thumb, t.id === active && styles.isActive].filter(Boolean).join(" ")}
              >
                <img src={t.thumb || t.image} alt="" className={styles.thumbImg} />
                <span className={styles.thumbName}>{t.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden className={styles.check}>
      <path d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.2 7.25a1 1 0 0 1-1.43.01L3.29 9.2a1 1 0 1 1 1.42-1.41l3.05 3.02 6.49-6.54a1 1 0 0 1 1.454.02Z" />
    </svg>
  );
}

// SVG fallback used when you haven't supplied real screenshots yet.
function sampleSVG(label) {
  return `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 1000'>
      <defs>
        <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0%' stop-color='#F5F7FF'/>
          <stop offset='100%' stop-color='#EEF2FF'/>
        </linearGradient>
      </defs>
      <rect width='100%' height='100%' fill='url(#g)'/>
      <rect x='72' y='120' width='656' height='80' rx='14' fill='#1F2937' opacity='.9'/>
      <rect x='72' y='230' width='360' height='18' rx='9' fill='#9CA3AF'/>
      <rect x='72' y='270' width='440' height='18' rx='9' fill='#CBD5E1'/>
      <rect x='72' y='310' width='520' height='18' rx='9' fill='#E5E7EB'/>

      <rect x='72' y='380' width='656' height='200' rx='16' fill='#FFFFFF' stroke='#E5E7EB'/>
      <rect x='96' y='410' width='280' height='16' rx='8' fill='#A78BFA'/>
      <rect x='96' y='440' width='520' height='12' rx='6' fill='#D1D5DB'/>
      <rect x='96' y='465' width='520' height='12' rx='6' fill='#E5E7EB'/>
      <rect x='96' y='490' width='420' height='12' rx='6' fill='#E5E7EB'/>

      <rect x='72' y='615' width='656' height='200' rx='16' fill='#FFFFFF' stroke='#E5E7EB'/>
      <rect x='96' y='642' width='240' height='16' rx='8' fill='#22C55E'/>
      <rect x='96' y='672' width='520' height='12' rx='6' fill='#D1D5DB'/>
      <rect x='96' y='697' width='520' height='12' rx='6' fill='#E5E7EB'/>
      <rect x='96' y='722' width='360' height='12' rx='6' fill='#E5E7EB'/>

      <g font-family='Inter, ui-sans-serif' font-size='36' font-weight='700' fill='#111827'>
        <text x='72' y='88'>${label} Resume</text>
      </g>
    </svg>
  `;
}  
